/** One registry record offered to the person as possibly theirs. */
export type NpiCandidate = {
  npi: string;
  firstName: string;
  lastName: string;
  middleName: string | null;
  credential: string | null;
  specialty: string | null;
  city: string | null;
  state: string | null;
};

/**
 * `single`: one candidate strong enough to ask "Is this you?" about.
 * `several`: a list to pick from. `none`: ask for the person's name.
 */
export type NpiSuggestionConfidence = 'single' | 'several' | 'none';

/**
 * Where a name guess came from: `typed` by the person, `identity_provider` from
 * their sign-in, `email_*` read from the email, and `surname_prefix` the
 * fallback for a surname the registry did not match exactly.
 */
export type NpiNameSource =
  | 'typed'
  | 'typed_initial'
  | 'identity_provider'
  | 'email_full'
  | 'email_initial'
  | 'surname_prefix';

export type NpiSuggestions = {
  /** Most likely first. */
  candidates: NpiCandidate[];
  confidence: NpiSuggestionConfidence;
  /** Nothing the server had yielded a name to search, so ask for one. */
  needsName: boolean;
  /** The name read from the email, to prefill the name field with. */
  bestNameGuess: { first: string; last: string } | null;
  /** The state the person's network placed them in, for acceptance analytics. */
  ipState: string | null;
  /** Where the best name guess came from, for analytics. */
  nameSource: NpiNameSource | null;
  /** Where the leading candidate's search came from, for analytics. */
  leadSource: NpiNameSource | null;
};

export type NpiSuggestionsRequest = {
  /** A name the person typed. */
  name?: string;
  /** A two-letter state the person picked. */
  state?: string;
  referralCode?: string;
};

/** `reading` guesses a name, `searching` asks the NPI registry, `comparing` orders the matches. */
/** A run's stages, in the order the server works through them. */
export const NPI_SUGGESTION_STAGES = ['reading', 'searching', 'comparing'] as const;

export type NpiSuggestionStage = (typeof NPI_SUGGESTION_STAGES)[number];

/**
 * A suggestion run as the server reports it. It is started once and read
 * back: its stage while it works, its suggestions when done.
 */
export type NpiSuggestionRun =
  | { runId: string; status: 'running'; stage: NpiSuggestionStage | null }
  | { runId: string; status: 'done'; suggestions: NpiSuggestions };

export const NO_SUGGESTIONS: NpiSuggestions = {
  candidates: [],
  confidence: 'none',
  needsName: false,
  bestNameGuess: null,
  ipState: null,
  nameSource: null,
  leadSource: null,
};
